/* eslint-disable @typescript-eslint/naming-convention */
import { cloneDeep, isNumber, pull, pullAll, toInteger } from 'lodash';
import { createDetachedAgentOperateConfig } from '@/utils/agent-teams-kit';
import {
    getCollisionIds,
    getIntersectionArea,
    getRotatePosition,
    getRotateRect,
    type IShape,
} from '@/utils/collision-kit';
import { traverseTrees } from '@/utils/tree-kit';
import { LayerType, type IAgentInfo, type IGroupLayerProps, type ILayerProps, type IPoint } from '@/version-types';
import { resetGroupLayerRotate, type IService } from '../../shared';
import { EventGenerator } from '../events';
import { HISTORY_CHANGE_EVENT } from '../history/event-name';
import type { IPptContainerBox } from '../ppt';
import type { ServiceManager } from '../service-manager';

interface IAgentLayoutOptions {
    /** 目标舞台 ID，不传时默认使用当前激活舞台 */
    frameId?: number;
    /** 每行最多图层数 */
    maxColumns?: number;
    /** 同组图层列间距 */
    columnGap?: number;
    /** 同组换行后的行间距 */
    rowGap?: number;
    /** 同 taskId 内不同 name 分组之间的间距 */
    groupGap?: number;
    /** 不同 taskId 容器之间的间距 */
    taskGap?: number;
}

interface IAgentLayoutFocusOptions {
    /** 目标舞台 ID，不传时默认使用当前激活舞台 */
    frameId?: number;
}

interface IAgentLayoutGroup {
    /** 分组 ID（对应 agent.id），用作布局分组标记 */
    id: string;
    name: string;
    layoutOrder?: number;
    left: number;
    top: number;
    layers: ILayerProps[];
}

interface IAgentLayoutTask {
    taskId: string;
    left: number;
    top: number;
    layers: ILayerProps[];
    groups: IAgentLayoutGroup[];
}

interface IAgentLayoutBounds {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

interface IAgentLayoutSize {
    width: number;
    height: number;
}

export interface IAgentBadgeData {
    key: string;
    taskId: string;
    groupId: string;
    left: number;
    top: number;
    name: string;
    avatar: string;
    groupBounds: {
        left: number;
        top: number;
        width: number;
        height: number;
        borderRadius: number;
    };
    placeholderGroupBounds?: {
        left: number;
        top: number;
        width: number;
        height: number;
        borderRadius: number;
    };
}

type IAgentLayoutGroupBounds = IAgentBadgeData['groupBounds'];

interface IAgentLayoutDragGroup {
    key: string;
    taskId: string;
    groupId: string;
    name: string;
    avatar: string;
    bounds: IAgentLayoutGroupBounds;
    nextLayoutOrder: number;
    groupLayoutOrder?: number;
}

interface IAgentLayoutDragState {
    layerId: string;
    agentLayerId: string;
    frameId: number;
    canSortAgentGroup: boolean;
    originDragLayerShape: IShape;
    originAgentLayer?: ILayerProps;
    originAgent?: IAgentInfo;
    originGroup?: IAgentLayoutDragGroup;
    originGroupLayers?: ILayerProps[];
    originGroupLayerIds?: string[];
    originGroupLayerShapes?: Map<string, IShape>;
    sortPreviewLayerIds?: string[] | null;
    layerIds: Set<string>;
}

interface IAgentLayoutDragResult {
    shouldArrange: boolean;
    frameIds: number[];
    retainOriginGroup?: boolean;
    skipHistory?: boolean;
    packHistory?: IAgentLayoutPackHistory;
    historyOldLayers?: ILayerProps[];
    historyNewLayers?: ILayerProps[];
}

interface IAgentLayoutPackHistory {
    type: string;
    data: {
        item: {
            childIndexes: number[];
            childIds: string[];
            layer: ILayerProps;
            layerIndex: number;
            oldLayer: ILayerProps;
            newLayer: ILayerProps;
        };
    };
    originIndex: number;
    trackFunc?: string;
    needSyncRenderIds: boolean;
}

interface IAgentProvider {
    getAgent(options: {
        taskId: string;
        id: string;
        name: string;
        avatar: string;
        downloadFileName?: string;
        relationID?: string;
    }): IAgentInfo;
}

const AGENT_LAYER_INSTANCE_MAP: Partial<Record<LayerType, string>> = {
    [LayerType.IMAGE_LAYER]: 'ImageLayer',
    [LayerType.MEDIA_LAYER]: 'MediaLayer',
    [LayerType.AUDIO_LAYER]: 'AudioLayer',
    [LayerType.FILE_LAYER]: 'FileLayer',
    [LayerType.PLAIN_TEXT_LAYER]: 'PlainTextLayer',
    [LayerType.MARKDOWN_LAYER]: 'MarkdownLayer',
    [LayerType.POST_LAYER]: 'PostLayer',
    [LayerType.POST_BRIEF_LAYER]: 'PostBriefLayer',
    [LayerType.PLACEHOLDER_LAYER]: 'PlaceholderLayer',
};

const GROUP_PACK_HISTORY_TYPE = 'pack:o-group';
const PPT_FRAME_LAYOUT_CHANGE_EVENT = 'agent-layout:ppt-frame-layout-change';

/** 设计基准画布宽度（像素），所有 agent 图层默认按此宽度设计 */
export const DEFAULT_BASELINE_CANVAS_WIDTH = 1024;
/** 同一 name 分组内每行最多展示的图层数 */
const DEFAULT_MAX_COLUMNS = 10;
/** 同一行内相邻图层的水平间距（基准） */
const DEFAULT_COLUMN_GAP = 16;
/** 同一 name 分组换行后的垂直间距（基准） */
const DEFAULT_ROW_GAP = 16;
/** 同一 taskId 下不同 name 分组之间的垂直间距（基准） */
export const DEFAULT_GROUP_GAP = 100;
/** 不同 taskId 容器之间的垂直间距（基准） */
const DEFAULT_TASK_GAP = 600;
/** 含占位图层的 agent group 边框与组内图层的间距（基准） */
const PLACEHOLDER_GROUP_BORDER_GAP = 8;
/** Agent group 内拖拽排序触发阈值：交叉面积 / 较小图层面积。 */
const AGENT_GROUP_SORT_INTERSECTION_RATIO = 1 / 3;
/** AI Agent 图层自动排版服务 */
export class AgentLayoutService implements IService {
    private __isArranging = false;

    private __dragState: IAgentLayoutDragState | null = null;

    /** 仅用于恢复排序的 group 顺序缓存，不能参与 badge 渲染。 */
    private __groupOrderCacheMap: Record<number, Record<string, number>> = {};

    /** task 首次进入 frame 时确定的顺序，图层删除或换入换出 frame 时不重排。 */
    private __taskOrderCacheMap: Record<number, Record<string, number>> = {};

    /** 是否启用自动排版 */
    readonly enable: boolean = false;
    declare getEnable: () => boolean;
    declare setEnable: (enable: boolean) => void;
    declare onEnableChange: (fn: (enable: boolean) => void) => number;

    /**
     * 设计基准画布宽度（像素）。
     * 所有 agent 图层（如 markdown / plain-text / audio / file / post / post-brief）
     * 的默认宽高、字号、内边距、间距、圆角等数值都是按此宽度设计的。
     */
    readonly baselineCanvasWidth: number = DEFAULT_BASELINE_CANVAS_WIDTH;
    declare getBaselineCanvasWidth: () => number;
    declare setBaselineCanvasWidth: (value: number) => void;
    declare onBaselineCanvasWidthChange: (fn: (value: number) => void) => number;

    /**
     * 当前实际画布宽度（像素）。
     * 通过修改此值可让 agent 图层渲染 / 操作 / 数据按比例缩放，
     * 例如设置为 2048 时，所有数值会按 2 倍缩放。
     */
    readonly canvasWidth: number = DEFAULT_BASELINE_CANVAS_WIDTH * 3;
    declare getCanvasWidth: () => number;
    declare setCanvasWidth: (value: number) => void;
    declare onCanvasWidthChange: (fn: (value: number) => void) => number;

    /** 按 frameId 缓存的 agent badge 数据 */
    readonly badgeMap: Record<number, IAgentBadgeData[]> = {};
    declare getBadgeMap: () => Record<number, IAgentBadgeData[]>;
    declare setBadgeMap: (badgeMap: Record<number, IAgentBadgeData[]>) => void;
    declare onBadgeMapChange: (fn: (badgeMap: Record<number, IAgentBadgeData[]>) => void) => number;

    /** 拖动过程中需要高亮的 agent group 边框 key。 */
    readonly activeGroupBorderKey: string = '';
    declare getActiveGroupBorderKey: () => string;
    declare setActiveGroupBorderKey: (key: string) => void;
    declare onActiveGroupBorderKeyChange: (fn: (key: string) => void) => number;

    constructor(readonly service: ServiceManager) {
        new EventGenerator('agent-layout', this, service);
    }

    /** 监听 PPT frame 布局变化，回调参数是通知时全部 PPT frame ID 的只读快照。 */
    onPPTFrameLayoutChange(listener: (frameIds: ReadonlySet<number>) => void) {
        return this.service.events.on(PPT_FRAME_LAYOUT_CHANGE_EVENT, listener);
    }

    /** 立即重排基准 frame 内所有 agent task 与 PPT 容器，撤销/重做后用于恢复布局一致性。 */
    arrangeBaseFrame() {
        if (!this.getEnable()) return;
        this.__arrangeFrame(this.__getBaseFrameId());
    }

    getDefaultPPTTaskId(frameId = this.__getBaseFrameId()) {
        const tasks = this.__getTasksByFrameIds(frameId);

        return this.__getFirstTaskId(frameId) || tasks[0]?.taskId;
    }

    /**
     * 提供配方排版使用的任务顺序，供 PPT 标题编号保持一致
     *
     * @param taskId - 配方任务 ID，空字符串表示未关联配方的 PPT 任务
     * @param frameId - 排版所在的舞台 ID，默认使用基准舞台
     * @returns 已记录的任务顺序；尚未排版的任务返回最大安全整数，供调用方兜底排序
     */
    getTaskOrder(taskId: string, frameId = this.__getBaseFrameId()) {
        return this.__getTaskOrder(frameId, taskId);
    }

    /** 按图层所在的 agent 布局 frame 重排，用于组内子图层变形后刷新外壳区域。 */
    arrangeLayerFrame(layerId: string) {
        if (!this.getEnable()) return;

        const layer = this.service.flatLayer.getLayer(layerId);
        if (!layer) return;

        const agentLayer = this.__getFormalGroupRootLayer(layer) || layer;
        if (!this.service.flatLayer.checkHasAgent(agentLayer)) return;

        this.__arrangeFrame(this.__getLayerFrameId(agentLayer));
    }

    /**
     * 获取当前画布相对于基准画布的缩放比例。
     * scale = canvasWidth / baselineCanvasWidth
     */
    getCanvasScale(): number {
        const baseline = this.getBaselineCanvasWidth();
        const current = this.getCanvasWidth();
        return current / baseline;
    }

    /** 将基准设计值按当前画布缩放 */
    scaleSize(value: number): number {
        return value * this.getCanvasScale();
    }

    /** 重置运行时状态与 badge 缓存。 */
    onInit() {
        this.__isArranging = false;
        this.__dragState = null;
        this.__groupOrderCacheMap = {};
        this.__taskOrderCacheMap = {};
        this.setBadgeMap({});
        this.setActiveGroupBorderKey('');
    }

    /** 监听 agent 图层变化，在增删时同步更新对应 frame 的自动排版与 badge 数据。 */
    onBindEvent() {
        this.service.frame.onLayersReplaced((frameIds) => {
            if (this.__isArranging) return;
            frameIds.forEach((frameId) => this.__arrangeFrame(frameId));
        });

        this.service.flatLayer.onChange((layer, _onlyUpdateBaseInfo, args) => {
            if (this.__isArranging) return;
            if (!this.getEnable()) return;
            if (this.__checkIsDraggingLayer(layer)) return;

            if (layer?.extraInfo?.agent) {
                this.__arrangeFrame(this.__getLayerFrameId(layer));
                return;
            }

            if (args.isDelete && isNumber(args.dirtyFrameId)) {
                this.__arrangeFrame(args.dirtyFrameId);
            }
        });

        this.service.frame.onRuntimeLayerChange((layer, _onlyUpdateBaseInfo, args) => {
            if (this.__isArranging) return;
            if (!this.getEnable()) return;
            if (this.__checkIsDraggingLayer(layer)) return;

            if (layer?.extraInfo?.agent || args.isDelete) {
                this.__arrangeFrame(args.frameId);
            }
        });

        this.service.events.on(HISTORY_CHANGE_EVENT, (args, historyItem) => {
            if (this.__isArranging) return;
            if (!this.getEnable()) return;
            if (!historyItem || args.isCustom || (!args.isRedo && !args.isUndo)) return;

            const activePickLayerId = this.__getHistoryChangedPickLayerId(historyItem.data);
            this.__getHistoryAgentFrameIds(historyItem.data).forEach((frameId) => this.__arrangeFrame(frameId));

            if (activePickLayerId && this.service.flatLayer.getLayer(activePickLayerId)) {
                this.service.frame.setActiveLayer(activePickLayerId, true);
            }
        });

        // 监听舞台重新渲染, 排序操作会触发
        this.service.frame.onSortLayer((frameId) => {
            if (this.__isArranging) return;
            if (!this.getEnable()) return;

            this.__arrangeFrame(frameId);
        });

        // 添加/删除 PPT frame 后，直接重排基准 frame，保证 PPT 容器和内容位置同步
        this.service.frame.onRerenderAll(() => {
            if (this.__isArranging) return;
            if (!this.getEnable()) return;
            if (!this.service.edgelessPPT.enable) return;

            this.__requestArrangeBaseFrame();
        }, 800);

        this.service.frame.onAddExtraInfos((extraInfos) => {
            if (this.__isArranging) return;
            if (!this.getEnable()) return;
            if (!this.service.edgelessPPT.enable) return;
            if (!extraInfos.some((extraInfo) => extraInfo?.isPPT)) return;

            this.__requestArrangeBaseFrame();
        });

        // PPT 服务启停时，重排基准 frame，保证 PPT 容器的显示与排版同步
        this.service.edgelessPPT.onEnableChange(() => {
            if (this.__isArranging) return;
            if (!this.getEnable()) return;

            requestAnimationFrame(() => {
                this.__arrangeFrame(this.__getBaseFrameId());
            });
        });
    }

    /**
     * 检查是否启用自动排版
     *
     * @param layer - 图层属性
     */
    checkArranging(layer: ILayerProps) {
        const { flatLayer } = this.service;
        return this.enable && flatLayer.checkHasAgent(layer);
    }

    /**
     * 根据 taskId 和 agent id 聚焦到对应分组。
     *
     * @param taskId - 任务 ID
     * @param id - agent ID（分组标记）
     * @param options - 聚焦配置
     */
    focusGroup(taskId: string, id: string, options?: IAgentLayoutFocusOptions) {
        const { frameId = this.service.frame.getActiveFrameId() } = options || {};
        const task = this.__getTasksByFrameIds(frameId).find((item) => item.taskId === taskId);
        const group = task?.groups.find((item) => item.id === id);
        const layerIds = group?.layers.map((layer) => layer.id) || [];

        if (!layerIds.length) return;

        return this.service.edgelessCanvas.setObjectToCenterByLayerId(layerIds);
    }

    /**
     * 根据图层关联标记查找图层。
     *
     * @param relationID - 图层关联标记
     * @param options - 查找配置
     * @returns 查找结果
     */
    findLayerByRelationID(relationID: string, options?: IAgentLayoutFocusOptions): ILayerProps | null {
        const { frameId = this.service.frame.getActiveFrameId() } = options || {};

        return this.__findLayerByRelationID(frameId, relationID);
    }

    /**
     * 查找指定 taskId 和 agent id 下的所有占位图层。
     *
     * @param taskId - 任务 ID
     * @param id - agent ID（分组标记）
     * @param options - 查找配置
     * @returns 匹配的占位图层列表
     */
    findPlaceholderLayers(taskId: string, id: string, options?: IAgentLayoutFocusOptions): ILayerProps[] {
        const { frameId = this.service.frame.getActiveFrameId() } = options || {};

        return this.__getAgentLayoutLayers(frameId).filter((layer) => {
            const agent = layer.extraInfo?.agent;

            return layer.layerType === LayerType.PLACEHOLDER_LAYER && agent?.taskId === taskId && agent.id === id;
        });
    }

    /**
     * 根据图层关联标记查找图层并聚焦。
     *
     * @param relationID - 图层关联标记
     * @param options - 聚焦配置
     * @returns 聚焦结果
     */
    focusLayerByRelationID(relationID: string, options?: IAgentLayoutFocusOptions) {
        const layer = this.findLayerByRelationID(relationID, options);

        if (!layer) return;

        this.service.frame.setActiveLayer(layer.id);

        return this.service.edgelessCanvas.setObjectToCenterByLayerId(layer.id);
    }

    /** 开始拖动图层时记录 group 边框快照，拖动中不跟随图层变化重算。 */
    beforeDragLayer(layerId: string) {
        if (!this.getEnable()) return;

        const layer = this.service.flatLayer.getLayer(layerId);
        if (!layer) return;

        const agentLayer = this.__getFormalGroupRootLayer(layer) || layer;
        const frameId = this.__getLayerFrameId(agentLayer);
        const originAgent = this.__getLayerAgent(agentLayer);
        const originGroup = originAgent
            ? this.__getGroupCollisionCandidates(frameId).find(
                  (group) => group.key === this.__getBadgeKey(frameId, originAgent.taskId, originAgent.id),
              )
            : undefined;
        const originGroupLayers = originGroup ? this.__getGroupLayoutLayers(frameId, originGroup) : [];
        const originGroupLayerIds = originGroupLayers.map((item) => item.id);
        const originGroupLayerShapes = new Map(originGroupLayers.map((item) => [item.id, this.__getLayerShape(item)]));
        const layerIds = new Set<string>();
        traverseTrees([layer], (item: ILayerProps) => {
            layerIds.add(item.id);
        });
        if (agentLayer.id !== layer.id) {
            traverseTrees([agentLayer], (item: ILayerProps) => {
                layerIds.add(item.id);
            });
        }

        this.__dragState = {
            layerId,
            agentLayerId: agentLayer.id,
            frameId,
            canSortAgentGroup: agentLayer.id === layer.id,
            originDragLayerShape: this.__getLayerShape(layer),
            originAgentLayer: cloneDeep(agentLayer),
            originAgent: originAgent ? cloneDeep(originAgent) : undefined,
            originGroup: originGroup ? cloneDeep(originGroup) : undefined,
            originGroupLayers: cloneDeep(originGroupLayers),
            originGroupLayerIds,
            originGroupLayerShapes,
            layerIds,
        };
        this.setActiveGroupBorderKey(originGroup?.key || '');
    }

    /** 拖动中依据当前图层 shape 和 group 矩形碰撞结果更新高亮边框。 */
    updateDragLayer(layerId: string) {
        if (!this.getEnable() || this.__dragState?.layerId !== layerId) return;

        const targetGroup = this.__getDragCollisionGroup(layerId);
        this.setActiveGroupBorderKey(targetGroup?.key || this.__dragState.originGroup?.key || '');
        if (this.__dragState.canSortAgentGroup) {
            this.__updateGroupSortPreview(this.__dragState.agentLayerId, targetGroup);
        }
    }

    /**
     * 取消本次 Agent 布局拖拽结算
     *
     * 恢复拖拽排序预览并清理碰撞状态，不改变图层父子关系或触发自动排版。
     *
     * @param layerId - 正在拖动的图层 ID
     */
    cancelDragLayer(layerId: string) {
        if (!this.getEnable() || this.__dragState?.layerId !== layerId) return;

        this.__restoreGroupSortPreview(this.__dragState);
        this.__dragState = null;
        this.setActiveGroupBorderKey('');
    }

    /** 拖动结束后按最终碰撞结果加入/脱离 group，返回后续需要重排的 frame。 */
    endDragLayer(layerId: string): IAgentLayoutDragResult | null {
        if (!this.getEnable() || this.__dragState?.layerId !== layerId) return null;

        const dragState = this.__dragState;
        const agentLayer = this.service.flatLayer.getLayer(dragState.agentLayerId);
        const frameId = agentLayer ? this.__getLayerFrameId(agentLayer) : dragState.frameId;
        const targetGroup = this.__getDragCollisionGroup(layerId);
        const shouldArrange = !!dragState.originAgent || !!targetGroup;
        const targetLayer =
            agentLayer && targetGroup ? this.__getDragResultAgentLayer(layerId, agentLayer, dragState) : null;
        const shouldSetExtraGroupChildrenAgent =
            !!targetLayer &&
            !!targetGroup &&
            this.service.flatLayer.checkIsExtraGroup(targetLayer) &&
            !this.__checkExtraGroupChildrenMatchTargetGroup(targetLayer, targetGroup);
        const sortResult =
            agentLayer &&
            dragState.canSortAgentGroup &&
            targetGroup?.key === dragState.originGroup?.key &&
            !shouldSetExtraGroupChildrenAgent
                ? this.__sortLayerInOriginGroup(agentLayer, dragState)
                : null;
        let historyOldLayers: ILayerProps[] | undefined = sortResult?.historyOldLayers;
        let historyNewLayers: ILayerProps[] | undefined = sortResult?.historyNewLayers;
        let packHistory: IAgentLayoutPackHistory | undefined;
        let arrangedInEndDrag = false;

        if (sortResult?.changed) {
            // group 内排序只更新 layoutOrder，具体位置由后续自动排版统一回写。
        } else if (
            agentLayer &&
            targetLayer &&
            targetGroup &&
            (targetGroup.key !== dragState.originGroup?.key || shouldSetExtraGroupChildrenAgent)
        ) {
            this.__restoreGroupSortPreview(dragState);

            if (this.service.flatLayer.checkIsExtraGroup(targetLayer)) {
                const extraGroupResult = this.__setExtraGroupChildrenAgent(targetLayer, targetGroup, dragState);

                historyOldLayers = extraGroupResult?.historyOldLayers;
                historyNewLayers = extraGroupResult?.historyNewLayers;
                arrangedInEndDrag = !!extraGroupResult;
            } else {
                const agent = this.__createLayerAgent(targetLayer, targetGroup);

                this.__setLayerAgent(targetLayer, agent);
                historyOldLayers = dragState.originAgentLayer ? [dragState.originAgentLayer] : undefined;
                historyNewLayers = this.__getAgentLayerHistory(targetLayer.id);
            }
        } else if (agentLayer && !dragState.canSortAgentGroup) {
            historyOldLayers = dragState.originAgentLayer ? [dragState.originAgentLayer] : undefined;
            historyNewLayers = this.__getAgentLayerHistory(agentLayer.id);
        } else if (agentLayer && dragState.originAgent && !targetGroup) {
            this.__restoreGroupSortPreview(dragState);
            this.__deleteLayerAgent(agentLayer);
            historyOldLayers = dragState.originAgentLayer ? [dragState.originAgentLayer] : undefined;
            historyNewLayers = this.__getAgentLayerHistory(agentLayer.id);
        } else if (!sortResult?.changed) {
            this.__restoreGroupSortPreview(dragState);
        }

        this.__dragState = null;
        this.setActiveGroupBorderKey('');

        return {
            shouldArrange: shouldArrange && !arrangedInEndDrag,
            frameIds: Array.from(new Set([dragState.frameId, frameId])),
            // 仍与原 Agent Group 碰撞时，Agent 回弹优先于 Frame 吸附。
            retainOriginGroup: !!dragState.originGroup && targetGroup?.key === dragState.originGroup.key,
            skipHistory: sortResult?.skipHistory,
            packHistory,
            historyOldLayers,
            historyNewLayers,
        };
    }

    /** 在历史记录采样完成后重排拖动影响到的 frame。 */
    arrangeDragResult(result: IAgentLayoutDragResult | null) {
        if (!result?.shouldArrange) return;

        result.frameIds.forEach((frameId) => this.__arrangeFrame(frameId));
    }

    /**
     * 重新计算指定 frame 内所有 agent task/group 的布局。
     * 先按 task 内部分组排版，再将不同 task 横向串联摆放。
     * PPT 容器按 taskId 分组，排到对应 task 分组最后。
     */
    private __arrangeFrame(frameId: number, options?: Omit<IAgentLayoutOptions, 'frameId'>) {
        if (!this.getEnable()) return;
        // 替换期间 children 与 flatLayer 暂时不同步，必须等完整写入后再排版和绘制。
        if (this.service.frame.isReplacingLayer) return;

        const scale = this.getCanvasScale();
        const {
            maxColumns = DEFAULT_MAX_COLUMNS,
            columnGap = DEFAULT_COLUMN_GAP * scale,
            rowGap = DEFAULT_ROW_GAP * scale,
            groupGap = DEFAULT_GROUP_GAP * scale,
            taskGap = DEFAULT_TASK_GAP * scale,
        } = options || {};
        const tasks = this.__getTasksByFrameIds(frameId);
        const isBaseFrame = frameId === this.__getBaseFrameId();
        const defaultPPTTaskId = this.__getFirstTaskId(frameId) || tasks[0]?.taskId;
        const pptFrameGroups =
            isBaseFrame && this.service.edgelessPPT.enable
                ? this.service.edgelessPPT.getPPTFrameGroups(defaultPPTTaskId)
                : [];
        const pptFrameGroupMap = new Map(pptFrameGroups.map((group) => [group.taskId || '', group.frameIds] as const));
        // 初始化时合并 Agent Group 与 PPT 容器的持久化坐标，确保纯 PPT task 也能恢复原顺序与锚点。
        const taskAnchorMap = new Map<string, IPoint>();

        tasks.forEach((task) => {
            taskAnchorMap.set(task.taskId, { x: task.left, y: task.top });
        });
        pptFrameGroups.forEach((group) => {
            const taskKey = group.taskId || '';
            const container = this.service.edgelessPPT.getContainer(group.frameIds);
            const previousAnchor = taskAnchorMap.get(taskKey);

            taskAnchorMap.set(taskKey, {
                x: Math.min(previousAnchor?.x ?? container.left, container.left),
                y: Math.min(previousAnchor?.y ?? container.top, container.top),
            });
        });
        this.__syncTaskOrders(
            Array.from(taskAnchorMap.entries())
                .sort((prev, next) => prev[1].x - next[1].x || prev[1].y - next[1].y)
                .map(([taskId]) => taskId),
            frameId,
        );
        const taskMap = new Map(tasks.map((task) => [task.taskId, task] as const));
        const orderedTaskKeys = Array.from(new Set([...taskMap.keys(), ...pptFrameGroupMap.keys()])).sort(
            (prev, next) => this.__getTaskOrder(frameId, prev) - this.__getTaskOrder(frameId, next),
        );
        const showPPTContainer = Boolean(pptFrameGroups.length);
        const previousTaskLeftMap = this.__getPreviousTaskLeftMap(frameId);
        const previousTaskTopMap = this.__getPreviousTaskTopMap(frameId);
        const arrangedExtraGroupLayerIds = new Set<string>();

        if (!tasks.length && !showPPTContainer) {
            if (isBaseFrame) {
                this.service.edgelessPPT.setContainerBoxes([]);
            }
            this.__updateBadgeMap(frameId, []);
            return;
        }

        try {
            this.__isArranging = true;
            const taskTop = tasks.length
                ? Math.min(...tasks.map((task) => previousTaskTopMap.get(task.taskId) ?? task.top))
                : Math.min(...Array.from(taskAnchorMap.values()).map((anchor) => anchor.y));
            let nextLeft: number | null = null;
            const pptContainers: IPptContainerBox[] = [];

            orderedTaskKeys.forEach((taskKey) => {
                const task = taskMap.get(taskKey);
                const pptFrameIds = pptFrameGroupMap.get(taskKey) || [];

                if (!task) {
                    const taskAnchor = taskAnchorMap.get(taskKey);
                    const pptContainer = this.__arrangePPTContainer({
                        taskId: taskKey || undefined,
                        frameIds: pptFrameIds,
                        containerLeft: nextLeft ?? taskAnchor?.x ?? null,
                        // task 内已没有 Agent Group 时，PPT 容器补位到 task 顶部，不保留原内容区域的空白。
                        containerTop: taskTop,
                    });

                    if (pptContainer) {
                        pptContainers.push(pptContainer);
                        nextLeft = pptContainer.left + pptContainer.width + taskGap;
                    }
                    return;
                }

                this.__syncTaskGroupLayoutOrders(task, frameId);
                this.__arrangeTaskGroups(
                    task,
                    {
                        maxColumns,
                        columnGap,
                        rowGap,
                        groupGap,
                        taskTop,
                    },
                    arrangedExtraGroupLayerIds,
                );
                const bounds = this.__getTaskBounds(task);
                const targetLeft = nextLeft ?? previousTaskLeftMap.get(task.taskId) ?? bounds.left;

                this.__moveTask(
                    task,
                    {
                        x: targetLeft,
                        y: taskTop,
                    },
                    arrangedExtraGroupLayerIds,
                );

                const taskBounds = this.__getTaskBounds(task);
                const pptContainerRight = pptFrameIds.length
                    ? taskBounds.left + this.__getPPTContainerSize(pptFrameIds).width
                    : taskBounds.right;

                if (pptFrameIds.length) {
                    const pptContainer = this.__arrangePPTContainer({
                        taskId: task.taskId,
                        frameIds: pptFrameIds,
                        containerLeft: taskBounds.left,
                        containerTop: taskBounds.bottom + groupGap,
                    });

                    if (pptContainer) {
                        pptContainers.push(pptContainer);
                    }
                }

                nextLeft = Math.max(taskBounds.right, pptContainerRight) + taskGap;
            });

            // 仅在基准 frame 上维护 PPT 容器，避免其它 frame 的重排误清空容器位置
            if (isBaseFrame) {
                this.service.edgelessPPT.setContainerBoxes(pptContainers);
            }

            this.__notifyPPTFrameLayoutChange(showPPTContainer);
            this.__updateBadgeMap(frameId, tasks);
            this.__refreshExtraGroupBounds(arrangedExtraGroupLayerIds);
            // 批量排版仅更新画布对象的基础信息，不会主动触发重绘。
            // 所有位置写回后统一渲染，避免图层回弹时选框与画布内容错位。
            this.service.edgelessCanvas.renderAll();
        } finally {
            this.__isArranging = false;
        }
    }

    private __syncTaskGroupLayoutOrders(task: IAgentLayoutTask, frameId: number) {
        const previousGroupOrderMap = this.__getPreviousGroupOrderMap(frameId);
        let maxOrder = Math.max(-1, ...Object.values(this.__groupOrderCacheMap[frameId] || {}));

        task.groups.forEach((group) => {
            const key = this.__getBadgeKey(frameId, task.taskId, group.id);
            const previousOrder = previousGroupOrderMap.get(key);
            const groupLayoutOrder = isNumber(group.layoutOrder)
                ? group.layoutOrder
                : isNumber(previousOrder)
                  ? previousOrder + 1
                  : ++maxOrder + 1;

            group.layoutOrder = groupLayoutOrder;
            group.layers.forEach((layer) => {
                this.__setLayerGroupLayoutOrder(layer, task.taskId, group.id, groupLayoutOrder, frameId);
            });
        });
    }

    /** 更新图层左上角，并同步平移其子图层。 */
    private __setLayerPosition(layer: ILayerProps, position: IPoint) {
        const targetLayer = this.service.flatLayer.get(layer.id) || layer;
        const left = toInteger(position.x);
        const top = toInteger(position.y);
        const offsetX = left - targetLayer.left;
        const offsetY = top - targetLayer.top;

        if (!offsetX && !offsetY) return false;

        targetLayer.left = left;
        targetLayer.top = top;

        if (targetLayer.children?.length) {
            traverseTrees(targetLayer.children, (child: ILayerProps) => {
                const childLayer = this.service.flatLayer.get(child.id) || child;
                childLayer.left = toInteger(childLayer.left + offsetX);
                childLayer.top = toInteger(childLayer.top + offsetY);
            });
        }

        if (targetLayer !== layer) {
            layer.left = targetLayer.left;
            layer.top = targetLayer.top;
        }

        return true;
    }

    /** 将图层树的位置变更写回 flatLayer / runtimeLayer。 */
    private __persistLayerTreePosition(layer: ILayerProps, isBatchArranging = false) {
        const runtimeLayer = this.service.frame.getRuntimeLayer(layer.id);

        if (runtimeLayer) {
            this.service.frame.addRuntimeLayer(layer, runtimeLayer.__originIndex, true);
            return;
        }

        const layersToPersist: ILayerProps[] = [];
        const rootLayer = this.service.flatLayer.get(layer.id) || layer;

        traverseTrees([rootLayer], (node: ILayerProps) => {
            const storedLayer = this.service.flatLayer.get(node.id) || node;

            if (storedLayer !== node) {
                storedLayer.left = node.left;
                storedLayer.top = node.top;
            }

            layersToPersist.push(storedLayer);
        });

        const persist = (targetLayer: ILayerProps) => {
            if (isBatchArranging) {
                this.service.flatLayer.set(targetLayer.id, targetLayer, {
                    onlyUpdateBaseInfo: true,
                    updateDependencies: false,
                });
                return;
            }

            try {
                this.__isArranging = true;
                this.service.flatLayer.set(targetLayer.id, targetLayer, {
                    onlyUpdateBaseInfo: true,
                    updateDependencies: false,
                });
            } finally {
                this.__isArranging = false;
            }
        };

        layersToPersist.forEach(persist);
    }

    /** 包装图层位置更新，控制是否以批量排版模式写回 flatLayer。 */
    private __updateLayerPosition(
        layer: ILayerProps,
        position: IPoint,
        isBatchArranging = false,
        arrangedExtraGroupLayerIds?: Set<string>,
    ) {
        const changed = this.__setLayerPosition(layer, position);

        if (!changed) return;

        if (isBatchArranging) {
            this.__collectArrangedExtraGroupLayer(layer, arrangedExtraGroupLayerIds);
        }
        this.__persistLayerTreePosition(layer, isBatchArranging);
    }

    private __collectArrangedExtraGroupLayer(layer: ILayerProps, arrangedExtraGroupLayerIds?: Set<string>) {
        if (!arrangedExtraGroupLayerIds) return;

        const parentId = layer.parentId || this.service.flatLayer.getLayer(layer.id)?.parentId;

        if (!parentId) return;

        const parentLayer = this.service.flatLayer.getLayer(parentId);

        if (this.service.flatLayer.checkIsExtraGroup(parentLayer)) {
            arrangedExtraGroupLayerIds.add(parentId);
        }
    }

    private __refreshExtraGroupBounds(extraGroupLayerIds: Set<string>) {
        extraGroupLayerIds.forEach((layerId) => {
            const layer = this.service.flatLayer.getLayer(layerId, true);

            if (!layer || !this.service.flatLayer.checkIsExtraGroup(layer)) return;

            this.service.frame.addOrUpdateLayer(layer, this.__getLayerFrameId(layer), true);
        });
    }

    /** 获取图层右边界。 */
    private __getRight(layer: ILayerProps) {
        return layer.left + layer.width;
    }

    /** 获取图层下边界。 */
    private __getLayerBottom(layer: ILayerProps) {
        return layer.top + layer.height;
    }

    /** 优先使用图层归属信息推断 frameId，兜底到当前激活 frame。 */
    private __getLayerFrameId(layer: ILayerProps) {
        return (
            layer.__originIndex ?? this.service.frame.getLayerFrameId(layer.id) ?? this.service.frame.getActiveFrameId()
        );
    }

    private __checkIsDraggingLayer(layer: ILayerProps | null) {
        if (!layer || !this.__dragState) return false;
        return this.__dragState.layerIds.has(layer.id);
    }

    private __getFormalGroupRootLayer(layer: ILayerProps) {
        let parentId = layer.parentId;
        let rootLayer: ILayerProps | null = null;

        while (parentId) {
            const parentLayer = this.service.flatLayer.getLayer(parentId);

            if (!parentLayer) break;
            if (this.service.flatLayer.checkIsFormalGroup(parentLayer)) {
                rootLayer = parentLayer;
            }

            parentId = parentLayer.parentId;
        }

        return rootLayer;
    }

    private __getAgentLayerHistory(layerId: string) {
        const layer = this.service.flatLayer.getLayer(layerId, true);

        return layer ? [layer] : undefined;
    }

    private __getDragResultAgentLayer(dragLayerId: string, agentLayer: ILayerProps, dragState: IAgentLayoutDragState) {
        if (dragLayerId === agentLayer.id || !dragState.originAgentLayer) return agentLayer;

        const dragLayer = this.service.flatLayer.getLayer(dragLayerId);

        if (!dragLayer) return agentLayer;

        const nextLayer = cloneDeep(dragState.originAgentLayer);
        const offsetX = dragLayer.left - dragState.originDragLayerShape.left;
        const offsetY = dragLayer.top - dragState.originDragLayerShape.top;

        traverseTrees([nextLayer], (layer: ILayerProps) => {
            layer.left = toInteger(layer.left + offsetX);
            layer.top = toInteger(layer.top + offsetY);
        });

        return nextLayer;
    }

    private __getBadgeKey(frameId: number, taskId: string, groupId: string) {
        return `${frameId}:${taskId}:${groupId}`;
    }

    private __getLayerAgent(layer: ILayerProps): IAgentInfo | undefined {
        if (layer.extraInfo?.agent) return layer.extraInfo.agent;

        let agent: IAgentInfo | undefined;
        traverseTrees(layer.children || [], (item: ILayerProps) => {
            if (!agent && item.extraInfo?.agent) {
                agent = item.extraInfo.agent;
            }
        });
        return agent;
    }

    private __getGroupCollisionCandidates(frameId: number): IAgentLayoutDragGroup[] {
        const groupOrderMap = this.__getGroupLayoutOrderMap(frameId);
        const previousGroupOrderMap = this.__getPreviousGroupOrderMap(frameId);

        return (this.badgeMap[frameId] || [])
            .filter((badge) => !!badge.groupBounds)
            .map((badge, index) => ({
                key: badge.key,
                taskId: badge.taskId,
                groupId: badge.groupId,
                name: badge.name,
                avatar: badge.avatar,
                bounds: badge.groupBounds,
                nextLayoutOrder: (groupOrderMap.get(badge.key) || 0) + 1,
                groupLayoutOrder: (previousGroupOrderMap.get(badge.key) ?? index) + 1,
            }));
    }

    private __getGroupLayoutOrderMap(frameId: number) {
        const orderMap = new Map<string, number>();

        this.__getAgentLayoutLayers(frameId).forEach((layer) => {
            const agent = layer.extraInfo?.agent;

            if (!agent?.taskId || !agent.id) return;

            const key = this.__getBadgeKey(frameId, agent.taskId, agent.id);
            const layoutOrder = agent.layoutOrder || 0;

            orderMap.set(key, Math.max(orderMap.get(key) || 0, layoutOrder));
        });

        return orderMap;
    }

    private __getPreviousTaskLeftMap(frameId: number) {
        const taskLeftMap = new Map<string, number>();

        (this.badgeMap[frameId] || []).forEach((badge) => {
            const currentLeft = taskLeftMap.get(badge.taskId);

            taskLeftMap.set(badge.taskId, isNumber(currentLeft) ? Math.min(currentLeft, badge.left) : badge.left);
        });

        return taskLeftMap;
    }

    private __getPreviousTaskTopMap(frameId: number) {
        const taskTopMap = new Map<string, number>();

        (this.badgeMap[frameId] || []).forEach((badge) => {
            const currentTop = taskTopMap.get(badge.taskId);

            taskTopMap.set(badge.taskId, isNumber(currentTop) ? Math.min(currentTop, badge.top) : badge.top);
        });

        return taskTopMap;
    }

    private __getPreviousGroupOrderMap(frameId: number) {
        const groupOrderMap = new Map<string, number>();

        Object.entries(this.__groupOrderCacheMap[frameId] || {}).forEach(([key, order]) =>
            groupOrderMap.set(key, order),
        );

        return groupOrderMap;
    }

    private __getGroupLayoutLayers(frameId: number, group: Pick<IAgentLayoutDragGroup, 'taskId' | 'groupId'>) {
        const task = this.__getTasksByFrameIds(frameId).find((item) => item.taskId === group.taskId);
        const targetGroup = task?.groups.find((item) => item.id === group.groupId);

        return targetGroup?.layers || [];
    }

    private __updateGroupSortPreview(layerId: string, targetGroup: IAgentLayoutDragGroup | null) {
        const dragState = this.__dragState;
        const layer = this.service.flatLayer.getLayer(layerId);

        if (!dragState?.originGroup || !layer) return;

        if (targetGroup?.key !== dragState.originGroup.key) {
            this.__restoreGroupSortPreview(dragState);
            return;
        }

        const previewLayerIds = this.__getGroupSortPreviewLayerIds(layer, dragState);

        if (!previewLayerIds) {
            this.__restoreGroupSortPreview(dragState);
            return;
        }

        if (this.__checkIsSameLayerIds(dragState.sortPreviewLayerIds, previewLayerIds)) return;

        dragState.sortPreviewLayerIds = previewLayerIds;
        this.__applyGroupSortPreview(dragState, previewLayerIds);
    }

    private __getGroupSortPreviewLayerIds(layer: ILayerProps, dragState: IAgentLayoutDragState) {
        const originLayerIds = dragState.originGroupLayerIds || [];
        const sourceIndex = originLayerIds.indexOf(layer.id);
        const targetIndex = this.__getGroupSortTargetIndex(layer, dragState);

        if (sourceIndex < 0 || targetIndex === null) return null;

        const previewLayerIds = this.__getSortPreviewLayerIds(originLayerIds, sourceIndex, targetIndex);
        const isSameOrder = previewLayerIds.every((layerId, index) => layerId === originLayerIds[index]);

        return isSameOrder ? null : previewLayerIds;
    }

    private __applyGroupSortPreview(dragState: IAgentLayoutDragState, previewLayerIds: string[]) {
        try {
            this.__isArranging = true;

            previewLayerIds.forEach((layerId, index) => {
                if (layerId === dragState.layerId) return;

                const layer = this.service.flatLayer.getLayer(layerId);
                const targetSlotLayerId = dragState.originGroupLayerIds?.[index];
                const targetShape = targetSlotLayerId ? dragState.originGroupLayerShapes?.get(targetSlotLayerId) : null;

                if (!layer || !targetShape) return;

                this.__updateLayerPosition(layer, { x: targetShape.left, y: targetShape.top }, true);
            });
        } finally {
            this.__isArranging = false;
        }

        this.service.edgelessCanvas.renderAll();
    }

    private __restoreGroupSortPreview(dragState: IAgentLayoutDragState) {
        if (!dragState.sortPreviewLayerIds) return;

        try {
            this.__isArranging = true;

            (dragState.originGroupLayerIds || []).forEach((layerId) => {
                if (layerId === dragState.layerId) return;

                const layer = this.service.flatLayer.getLayer(layerId);
                const originShape = dragState.originGroupLayerShapes?.get(layerId);

                if (!layer || !originShape) return;

                this.__updateLayerPosition(layer, { x: originShape.left, y: originShape.top }, true);
            });
        } finally {
            this.__isArranging = false;
        }

        dragState.sortPreviewLayerIds = null;
        this.service.edgelessCanvas.renderAll();
    }

    private __checkIsSameLayerIds(prevLayerIds: string[] | null | undefined, nextLayerIds: string[]) {
        return (
            !!prevLayerIds &&
            prevLayerIds.length === nextLayerIds.length &&
            prevLayerIds.every((layerId, index) => layerId === nextLayerIds[index])
        );
    }

    private __sortLayerInOriginGroup(layer: ILayerProps, dragState: IAgentLayoutDragState) {
        const originLayerIds = dragState.originGroupLayerIds || [];
        const sourceIndex = originLayerIds.indexOf(layer.id);
        const targetIndex = this.__getGroupSortTargetIndex(layer, dragState);

        if (sourceIndex < 0 || targetIndex === null) {
            return {
                changed: false,
                skipHistory: true,
            };
        }

        const nextLayerIds = this.__getSortPreviewLayerIds(originLayerIds, sourceIndex, targetIndex);
        const isSameOrder = nextLayerIds.every((layerId, index) => layerId === originLayerIds[index]);

        if (isSameOrder) {
            return {
                changed: false,
                skipHistory: true,
            };
        }

        const orderMap = new Map(nextLayerIds.map((layerId, index) => [layerId, index + 1]));
        const frameId = this.__getLayerFrameId(layer);
        const historyOldLayers = cloneDeep(dragState.originGroupLayers || []);

        try {
            this.__isArranging = true;
            originLayerIds.forEach((layerId) => {
                const targetLayer = this.service.flatLayer.getLayer(layerId, true);
                const layoutOrder = orderMap.get(layerId);

                if (!targetLayer || !layoutOrder || !dragState.originGroup) return;

                this.__setLayerLayoutOrder(targetLayer, dragState.originGroup, layoutOrder, frameId);
            });
        } finally {
            this.__isArranging = false;
        }

        return {
            changed: true,
            historyOldLayers,
            historyNewLayers: originLayerIds
                .map((layerId) => this.service.flatLayer.getLayer(layerId, true))
                .filter((item): item is ILayerProps => !!item),
        };
    }

    private __getGroupSortTargetIndex(layer: ILayerProps, dragState: IAgentLayoutDragState) {
        const layerIds = dragState.originGroupLayerIds || [];
        const layerShape = this.__getLayerShape(layer);
        const layerRect = getRotateRect({ ...layerShape, rotate: layerShape.rotate || 0 });
        const layerArea = layerShape.width * layerShape.height;
        let targetIndex: number | null = null;
        let maxIntersectionArea = 0;

        layerIds.forEach((targetLayerId, index) => {
            if (targetLayerId === layer.id) return;

            const targetLayer = this.service.flatLayer.getLayer(targetLayerId);
            const targetShape =
                dragState.originGroupLayerShapes?.get(targetLayerId) ||
                (targetLayer ? this.__getLayerShape(targetLayer) : null);

            if (!targetShape) return;

            const targetRect = getRotateRect({ ...targetShape, rotate: targetShape.rotate || 0 });
            const intersectionArea = getIntersectionArea(layerRect, targetRect);
            const targetArea = targetShape.width * targetShape.height;
            const threshold = Math.min(layerArea, targetArea) * AGENT_GROUP_SORT_INTERSECTION_RATIO;

            if (intersectionArea > threshold && intersectionArea > maxIntersectionArea) {
                targetIndex = index;
                maxIntersectionArea = intersectionArea;
            }
        });

        return targetIndex;
    }

    private __getSortPreviewLayerIds(layerIds: string[], sourceIndex: number, targetIndex: number) {
        const sourceLayerId = layerIds[sourceIndex];
        const nextLayerIds = layerIds.filter((_, index) => index !== sourceIndex);
        const targetLayerId = layerIds[targetIndex];
        const insertIndex = nextLayerIds.indexOf(targetLayerId);

        nextLayerIds.splice(sourceIndex > targetIndex ? insertIndex : insertIndex + 1, 0, sourceLayerId);

        return nextLayerIds;
    }

    private __setLayerLayoutOrder(
        layer: ILayerProps,
        group: Pick<IAgentLayoutDragGroup, 'taskId' | 'groupId'>,
        layoutOrder: number,
        frameId: number,
    ) {
        let changed = false;

        traverseTrees([layer], (item: ILayerProps) => {
            const agent = item.extraInfo?.agent;

            if (!agent || agent.taskId !== group.taskId || agent.id !== group.groupId) return;

            item.extraInfo = {
                ...item.extraInfo,
                agent: {
                    ...agent,
                    layoutOrder,
                },
            };
            changed = true;
        });

        if (changed) {
            this.service.frame.addOrUpdateLayer(layer, frameId, true);
        }
    }

    private __setLayerGroupLayoutOrder(
        layer: ILayerProps,
        taskId: string,
        groupId: string,
        groupLayoutOrder: number,
        frameId: number,
    ) {
        let changed = false;

        traverseTrees([layer], (item: ILayerProps) => {
            const agent = item.extraInfo?.agent;

            if (!agent || agent.taskId !== taskId || agent.id !== groupId) return;
            if (agent.groupLayoutOrder === groupLayoutOrder) return;

            item.extraInfo = {
                ...item.extraInfo,
                agent: {
                    ...agent,
                    groupLayoutOrder,
                },
            };
            changed = true;
        });

        if (changed) {
            this.service.frame.addOrUpdateLayer(layer, frameId, true);
        }
    }

    private __getHistoryAgentFrameIds(data: {
        oldLayer?: ILayerProps;
        newLayer?: ILayerProps;
        oldLayers?: ILayerProps[];
        newLayers?: ILayerProps[];
    }) {
        const frameIds = new Set<number>();
        const layers = [data.oldLayer, data.newLayer, ...(data.oldLayers || []), ...(data.newLayers || [])];

        layers.forEach((layer) => {
            if (!layer || !this.__getLayerAgent(layer)) return;

            const frameId = this.service.frame.getLayerFrameId(layer.id);
            if (isNumber(frameId)) {
                frameIds.add(frameId);
            } else if (isNumber(layer.__originIndex)) {
                frameIds.add(layer.__originIndex);
            }
        });

        return Array.from(frameIds);
    }

    private __getHistoryChangedPickLayerId(data: {
        oldLayer?: ILayerProps;
        newLayer?: ILayerProps;
        oldLayers?: ILayerProps[];
        newLayers?: ILayerProps[];
    }) {
        const oldLayers = data.oldLayers || (data.oldLayer ? [data.oldLayer] : []);
        const newLayers = data.newLayers || (data.newLayer ? [data.newLayer] : []);

        for (let i = 0; i < oldLayers.length; i++) {
            const oldLayer = oldLayers[i];
            const newLayer = newLayers[i];
            const changedPickLayerId = this.__getChangedPickLayerId(oldLayer, newLayer);

            if (changedPickLayerId) return changedPickLayerId;
        }

        return '';
    }

    private __getChangedPickLayerId(oldLayer?: ILayerProps, newLayer?: ILayerProps) {
        if (!oldLayer || !newLayer || oldLayer.id !== newLayer.id) return '';
        if (!this.service.flatLayer.checkIsFormalGroup(oldLayer) || !this.__getLayerAgent(oldLayer)) return '';

        const oldLayerMap = new Map<string, ILayerProps>();
        const rootOffsetX = newLayer.left - oldLayer.left;
        const rootOffsetY = newLayer.top - oldLayer.top;
        let changedPickLayerId = '';
        let hasUnchangedChildLayer = false;

        traverseTrees(oldLayer.children || [], (layer: ILayerProps) => {
            oldLayerMap.set(layer.id, layer);
        });
        traverseTrees(newLayer.children || [], (layer: ILayerProps) => {
            const oldChildLayer = oldLayerMap.get(layer.id);
            if (!oldChildLayer) return;
            if (!this.__checkLayerBaseInfoChanged(oldChildLayer, layer)) {
                hasUnchangedChildLayer = true;
                return;
            }

            const isFollowRootMove =
                layer.left - oldChildLayer.left === rootOffsetX &&
                layer.top - oldChildLayer.top === rootOffsetY &&
                layer.width === oldChildLayer.width &&
                layer.height === oldChildLayer.height &&
                layer.rotate === oldChildLayer.rotate;

            if (!isFollowRootMove && !changedPickLayerId) {
                changedPickLayerId = layer.id;
            }
        });

        return hasUnchangedChildLayer ? changedPickLayerId : '';
    }

    private __checkLayerBaseInfoChanged(oldLayer: ILayerProps, newLayer: ILayerProps) {
        return (
            oldLayer.left !== newLayer.left ||
            oldLayer.top !== newLayer.top ||
            oldLayer.width !== newLayer.width ||
            oldLayer.height !== newLayer.height ||
            oldLayer.rotate !== newLayer.rotate
        );
    }

    private __getDragCollisionGroup(layerId: string) {
        const layer = this.service.flatLayer.getLayer(layerId);
        const dragState = this.__dragState;

        if (!layer || !dragState) return null;

        const frameId = this.__getLayerFrameId(layer);
        const layerShape = this.__getLayerShape(layer);
        const groupCandidates = this.__getGroupCollisionCandidates(frameId);
        const candidates = dragState.originGroup
            ? [dragState.originGroup, ...groupCandidates.filter((group) => group.key !== dragState.originGroup?.key)]
            : groupCandidates;
        const groupShapes = candidates.map((group) => this.__getGroupShape(group));
        const collisionKey = getCollisionIds(layerShape, groupShapes, true)[0];

        return candidates.find((group) => group.key === collisionKey) || null;
    }

    private __getLayerShape(layer: ILayerProps): IShape {
        return {
            id: layer.id,
            left: layer.left,
            top: layer.top,
            width: layer.width,
            height: layer.height,
            rotate: layer.rotate,
        };
    }

    private __getGroupShape(group: IAgentLayoutDragGroup): IShape {
        return {
            id: group.key,
            left: group.bounds.left,
            top: group.bounds.top,
            width: group.bounds.width,
            height: group.bounds.height,
            rotate: 0,
        };
    }

    private __createLayerAgent(layer: ILayerProps, group: IAgentLayoutDragGroup): IAgentInfo {
        const providerKey = AGENT_LAYER_INSTANCE_MAP[layer.layerType] || 'Layer';
        const provider =
            this.service.registration.getInstance<IAgentProvider>(providerKey) ||
            this.service.registration.getInstance<IAgentProvider>('Layer');

        if (!provider) {
            throw new Error('Layer agent provider is not found');
        }

        return {
            ...provider.getAgent({
                taskId: group.taskId,
                id: group.groupId,
                name: group.name,
                avatar: group.avatar,
                downloadFileName: layer.extraInfo?.agent?.downloadFileName,
            }),
            layoutOrder: group.nextLayoutOrder,
            groupLayoutOrder: group.groupLayoutOrder,
        };
    }

    private __checkExtraGroupChildrenMatchTargetGroup(layer: ILayerProps, group: IAgentLayoutDragGroup) {
        const children = layer.children || [];

        if (!children.length) return false;

        return children.every((child) => {
            const agent = this.__getLayerAgent(child);

            return !!agent && agent.taskId === group.taskId && agent.id === group.groupId;
        });
    }

    private __setExtraGroupChildrenAgent(
        layer: ILayerProps,
        group: IAgentLayoutDragGroup,
        dragState: IAgentLayoutDragState,
    ) {
        const nextLayer = cloneDeep(layer);
        const originIndex = this.__getLayerFrameId(layer);
        const oldLayer = cloneDeep(dragState.originAgentLayer || layer);

        if (!nextLayer.children?.length) return;

        if (nextLayer.extraInfo?.agent) {
            nextLayer.extraInfo = {
                ...nextLayer.extraInfo,
            };
            delete nextLayer.extraInfo.agent;
        }

        (nextLayer.data as IGroupLayerProps).isExtraGroup = true;
        nextLayer.children.forEach((child, index) => {
            const agent = this.__createLayerAgent(child, {
                ...group,
                nextLayoutOrder: group.nextLayoutOrder + index,
            });

            if (this.service.flatLayer.checkIsFormalGroup(child)) {
                this.__resetAgentGroupLayerRotate(child);
            } else {
                child.rotate = 0;
            }
            this.__setLayerAgentInfo(child, agent);
        });
        this.service.frame.addOrUpdateLayer(nextLayer, originIndex, true);
        Array.from(new Set([dragState.frameId, originIndex])).forEach((frameId) => this.__arrangeFrame(frameId));

        const newLayer = this.service.flatLayer.getLayer(nextLayer.id, true) || nextLayer;

        return this.__getExtraGroupChildrenAgentHistory(oldLayer, newLayer);
    }

    private __getExtraGroupChildrenAgentHistory(oldLayer: ILayerProps, newLayer: ILayerProps) {
        const oldChildMap = new Map((oldLayer.children || []).map((child) => [child.id, child]));
        const newChildMap = new Map((newLayer.children || []).map((child) => [child.id, child]));
        const childIds = Array.from(new Set([...oldChildMap.keys(), ...newChildMap.keys()]));
        const historyOldLayers = [oldLayer];
        const historyNewLayers = [newLayer];

        childIds.forEach((childId) => {
            const oldChild = oldChildMap.get(childId);
            const newChild = newChildMap.get(childId) || this.service.flatLayer.getLayer(childId, true);

            if (!oldChild || !newChild) return;

            historyOldLayers.push(cloneDeep(oldChild));
            historyNewLayers.push(cloneDeep(newChild));
        });

        return {
            historyOldLayers,
            historyNewLayers,
        };
    }

    private __packExtraGroupLayerToAgent(
        layer: ILayerProps,
        agent: IAgentInfo,
        dragState: IAgentLayoutDragState,
    ): IAgentLayoutPackHistory | undefined {
        const nextLayer = cloneDeep(layer);
        const originIndex = this.__getLayerFrameId(layer);
        const originIds = this.service.frame.getLayerIds(originIndex);
        const childIndexes: number[] = [];
        const childIds: string[] = [];

        nextLayer.children.forEach((child) => {
            const index = originIds.indexOf(child.id);

            if (index !== -1) {
                childIndexes.push(index);
                childIds.push(child.id);
            }
        });

        if (!childIds.length) return;

        const maxChildNextLayerId = originIds[Math.max(...childIndexes) + 1];

        pullAll(originIds, childIds);

        if (maxChildNextLayerId !== nextLayer.id) {
            pull(originIds, nextLayer.id);

            const insertIndex = originIds.indexOf(maxChildNextLayerId);

            if (insertIndex === -1) {
                originIds.push(nextLayer.id);
            } else {
                originIds.splice(insertIndex, 0, nextLayer.id);
            }
        }

        (nextLayer.data as IGroupLayerProps).isExtraGroup = false;
        this.__setLayerAgentInfo(nextLayer, agent);
        this.__resetAgentGroupLayerRotate(nextLayer);
        this.service.frame.addOrUpdateLayer(nextLayer, originIndex, true);

        const oldLayer = cloneDeep(dragState.originAgentLayer || layer);
        const newLayer = cloneDeep(nextLayer);

        return {
            type: GROUP_PACK_HISTORY_TYPE,
            data: {
                item: {
                    childIndexes,
                    childIds,
                    layer: cloneDeep(newLayer),
                    layerIndex: originIds.indexOf(nextLayer.id),
                    oldLayer,
                    newLayer,
                },
            },
            originIndex,
            needSyncRenderIds: true,
        };
    }

    private __setLayerAgent(layer: ILayerProps, agent: IAgentInfo) {
        const nextLayer = cloneDeep(layer);

        if (this.service.flatLayer.checkIsFormalGroup(nextLayer)) {
            this.__resetAgentGroupLayerRotate(nextLayer);
        } else {
            nextLayer.rotate = 0;
        }
        this.__setLayerAgentInfo(nextLayer, agent);

        this.__addOrUpdateAgentLayer(nextLayer, this.__getLayerFrameId(layer));
    }

    private __setLayerAgentInfo(layer: ILayerProps, agent: IAgentInfo) {
        layer.extraInfo = {
            ...layer.extraInfo,
            agent,
        };
        delete layer.extraInfo.agentOperateConfig;
    }

    private __resetAgentGroupLayerRotate(layer: ILayerProps) {
        if (!layer.rotate) return;

        const positions: IPoint[] = [];

        (layer.children || []).forEach((child) => {
            positions.push(...getRotatePosition(child));
        });

        layer.rotate = 0;

        if (positions.length) {
            resetGroupLayerRotate(layer, positions);
        }
    }

    private __deleteLayerAgent(layer: ILayerProps) {
        const nextLayer = cloneDeep(layer);

        traverseTrees([nextLayer], (item: ILayerProps) => {
            if (item.extraInfo?.agent) {
                const agentOperateConfig = createDetachedAgentOperateConfig(item.extraInfo.agent);

                if (agentOperateConfig) {
                    item.extraInfo.agentOperateConfig = agentOperateConfig;
                }
                delete item.extraInfo.agent;
            }
        });

        this.__addOrUpdateAgentLayer(nextLayer, this.__getLayerFrameId(layer));
    }

    private __addOrUpdateAgentLayer(layer: ILayerProps, frameId: number) {
        if (this.service.flatLayer.checkIsFormalGroup(layer)) {
            traverseTrees([layer], (item: ILayerProps) => {
                item.__originIndex = frameId;
                this.service.flatLayer.set(item.id, item, {
                    onlyUpdateBaseInfo: true,
                    updateDependencies: false,
                });
            });
            return;
        }

        this.service.frame.addOrUpdateLayer(layer, frameId, true);
    }

    /** 获取承载无边界画布内容的基础 frameId。 */
    private __getBaseFrameId() {
        return this.service.frame.getRenderFrameIds()[0] ?? this.service.frame.getActiveFrameId();
    }

    /** 下一帧重排基准 frame，等待 frame 服务完成本轮状态刷新。 */
    private __requestArrangeBaseFrame() {
        requestAnimationFrame(() => {
            this.__arrangeFrame(this.__getBaseFrameId());
        });
    }

    /** 获取参与 agent 自动排版的 frame 图层，包含普通图层与 runtime 图层。 */
    private __getAgentLayoutLayers(frameId: number) {
        const layers = this.service.frame
            .getLayerIds(frameId)
            .map((layerId) => this.service.flatLayer.get(layerId))
            .filter((layer): layer is ILayerProps => !!layer);
        const runtimeLayers = this.service.frame.getRuntimeLayer(frameId);

        return this.__mergeFollowPlaceholderLayers(layers, runtimeLayers);
    }

    /** 仅占位图层支持按 relationID 跟随普通图层参与排版。 */
    private __mergeFollowPlaceholderLayers(layers: ILayerProps[], runtimeLayers: ILayerProps[]) {
        const followLayerMap = new Map<string, ILayerProps[]>();
        const restRuntimeLayers: ILayerProps[] = [];

        runtimeLayers.forEach((layer) => {
            const followKey = this.__getPlaceholderFollowKey(layer);

            if (!followKey) {
                restRuntimeLayers.push(layer);
                return;
            }

            followLayerMap.set(followKey, [...(followLayerMap.get(followKey) || []), layer]);
        });

        const mergedLayers = layers.flatMap((layer) => {
            const followKey = this.__getLayerFollowKey(layer, layer.id);
            const followLayers = followKey ? followLayerMap.get(followKey) : null;

            if (!followLayers?.length) return [layer];

            followLayerMap.delete(followKey);
            return [layer, ...followLayers];
        });

        followLayerMap.forEach((followLayers) => {
            restRuntimeLayers.push(...followLayers);
        });

        return mergedLayers.concat(restRuntimeLayers);
    }

    /** 获取占位图层的跟随标识。 */
    private __getPlaceholderFollowKey(layer: ILayerProps) {
        if (layer.layerType !== LayerType.PLACEHOLDER_LAYER) return '';

        return this.__getLayerFollowKey(layer, layer.extraInfo?.agent?.relationID);
    }

    /** 获取图层的 agent 分组跟随标识。 */
    private __getLayerFollowKey(layer: ILayerProps, relationID?: string) {
        const agent = layer.extraInfo?.agent;

        if (!agent?.taskId || !agent.id || !relationID) return '';

        return `${agent.taskId}:${agent.id}:${relationID}`;
    }

    /** 按 frame 中图层顺序聚合 task，并保留各 task 下的 agent 分组信息。 */
    private __getTasksByFrameIds(frameId: number) {
        const taskMap = new Map<string, IAgentLayoutTask>();

        this.__getAgentLayoutLayers(frameId).forEach((layer) => {
            const agent = layer?.extraInfo?.agent;

            if (!layer || !agent?.taskId || !agent.id) return;

            let task = taskMap.get(agent.taskId);

            if (!task) {
                task = {
                    taskId: agent.taskId,
                    left: layer.left,
                    top: layer.top,
                    layers: [],
                    groups: [],
                };
                taskMap.set(agent.taskId, task);
            }

            task.left = Math.min(task.left, layer.left);
            task.top = Math.min(task.top, layer.top);
            task.layers.push(layer);
            this.__appendGroupLayer(task, layer, agent);
        });

        return this.__sortTasks(Array.from(taskMap.values()), frameId);
    }

    private __syncTaskOrders(taskIds: string[], frameId: number) {
        const previousOrderCache = this.__taskOrderCacheMap[frameId] || {};
        const nextOrderCache = { ...previousOrderCache };
        let maxOrder = Math.max(-1, ...Object.values(previousOrderCache));

        taskIds.forEach((taskId) => {
            if (!isNumber(nextOrderCache[taskId])) {
                nextOrderCache[taskId] = ++maxOrder;
            }
        });

        this.__taskOrderCacheMap = {
            ...this.__taskOrderCacheMap,
            [frameId]: nextOrderCache,
        };
    }

    private __getTaskOrder(frameId: number, taskId: string) {
        return this.__taskOrderCacheMap[frameId]?.[taskId] ?? Number.MAX_SAFE_INTEGER;
    }

    private __getFirstTaskId(frameId: number) {
        return Object.entries(this.__taskOrderCacheMap[frameId] || {})
            .filter(([taskId]) => !!taskId)
            .sort((prev, next) => prev[1] - next[1])[0]?.[0];
    }

    private __sortTasks(tasks: IAgentLayoutTask[], frameId: number) {
        const previousGroupOrderMap = this.__getPreviousGroupOrderMap(frameId);

        tasks.sort((prev, next) => {
            const prevOrder = this.__getTaskOrder(frameId, prev.taskId);
            const nextOrder = this.__getTaskOrder(frameId, next.taskId);

            if (prevOrder !== nextOrder) return prevOrder - nextOrder;

            return prev.left - next.left || prev.top - next.top;
        });

        tasks.forEach((task) => {
            task.groups = task.groups
                .map((group, index) => ({ group, index }))
                .sort((prev, next) => {
                    const prevOrder = previousGroupOrderMap.get(
                        this.__getBadgeKey(frameId, task.taskId, prev.group.id),
                    );
                    const nextOrder = previousGroupOrderMap.get(
                        this.__getBadgeKey(frameId, task.taskId, next.group.id),
                    );
                    const prevSortOrder = isNumber(prevOrder)
                        ? prevOrder
                        : (prev.group.layoutOrder ?? Number.MAX_SAFE_INTEGER);
                    const nextSortOrder = isNumber(nextOrder)
                        ? nextOrder
                        : (next.group.layoutOrder ?? Number.MAX_SAFE_INTEGER);

                    if (prevSortOrder !== nextSortOrder) return prevSortOrder - nextSortOrder;

                    return prev.index - next.index;
                })
                .map((item) => item.group);

            task.groups.forEach((group) => {
                group.layers = group.layers
                    .map((layer, index) => ({ layer, index }))
                    .sort((prev, next) => {
                        const prevOrder = prev.layer.extraInfo?.agent?.layoutOrder || 0;
                        const nextOrder = next.layer.extraInfo?.agent?.layoutOrder || 0;

                        if (prevOrder !== nextOrder) return prevOrder - nextOrder;

                        return prev.index - next.index;
                    })
                    .map((item) => item.layer);
            });
        });

        return tasks;
    }

    /** 根据图层关联标记查找 frame 内的目标图层。 */
    private __findLayerByRelationID(frameId: number, relationID: string): ILayerProps | null {
        for (const layer of this.__getAgentLayoutLayers(frameId)) {
            let matchedLayer: ILayerProps | null = null;

            traverseTrees([layer], (currentLayer: ILayerProps) => {
                if (!matchedLayer && this.__isSameRelationID(currentLayer, relationID)) {
                    matchedLayer = currentLayer;
                }
            });

            if (matchedLayer) {
                return matchedLayer;
            }
        }

        return null;
    }

    /** 判断图层上的关联标记是否匹配。 */
    private __isSameRelationID(layer: ILayerProps, relationID: string) {
        return !!layer.extraInfo?.agent?.relationID && layer.extraInfo.agent.relationID === relationID;
    }

    /** 将图层归入指定 task 的 id 分组，同时维护分组左上角坐标。 */
    private __appendGroupLayer(task: IAgentLayoutTask, layer: ILayerProps, agent: IAgentInfo) {
        const { id, name } = agent;
        let group = task.groups.find((item) => item.id === id);

        if (!group) {
            group = {
                id,
                name,
                layoutOrder: agent.groupLayoutOrder,
                left: layer.left,
                top: layer.top,
                layers: [],
            };
            task.groups.push(group);
        }

        if (isNumber(agent.groupLayoutOrder)) {
            group.layoutOrder = isNumber(group.layoutOrder)
                ? Math.min(group.layoutOrder, agent.groupLayoutOrder)
                : agent.groupLayoutOrder;
        }
        group.left = Math.min(group.left, layer.left);
        group.top = Math.min(group.top, layer.top);
        group.layers.push(layer);
    }

    /** 计算一组图层的外接矩形。 */
    private __getTaskBounds(target: ILayerProps[] | IAgentLayoutTask): IAgentLayoutBounds {
        const layers = Array.isArray(target) ? target : target.layers;
        const boundsList: IAgentLayoutBounds[] = [];

        if (layers.length) {
            boundsList.push({
                left: Math.min(...layers.map((layer) => layer.left)),
                top: Math.min(...layers.map((layer) => layer.top)),
                right: Math.max(...layers.map((layer) => this.__getRight(layer))),
                bottom: Math.max(...layers.map((layer) => this.__getLayerBottom(layer))),
            });
        }

        if (!boundsList.length) {
            return {
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
            };
        }

        return {
            left: Math.min(...boundsList.map((bounds) => bounds.left)),
            top: Math.min(...boundsList.map((bounds) => bounds.top)),
            right: Math.max(...boundsList.map((bounds) => bounds.right)),
            bottom: Math.max(...boundsList.map((bounds) => bounds.bottom)),
        };
    }

    /** 计算 PPT 容器尺寸，不移动 PPT frame。 */
    private __getPPTContainerSize(frameIds = this.service.edgelessPPT.getPPTFrameIds()): IAgentLayoutSize {
        const { edgelessPPT } = this.service;
        const padding = edgelessPPT.containerPadding;

        if (!frameIds.length) {
            const container = edgelessPPT.getContainer([]);

            return {
                width: container.width,
                height: container.height,
            };
        }

        let maxRight = 0;
        let maxBottom = 0;

        frameIds.forEach((frameId, index) => {
            const shape = this.service.frame.getShape(frameId);
            const position = edgelessPPT.getPosition(index, frameIds);

            maxRight = Math.max(maxRight, position.left + shape.width);
            maxBottom = Math.max(maxBottom, position.top + shape.height);
        });

        return {
            width: maxRight + padding * 2,
            height: maxBottom + padding * 2,
        };
    }

    /** 根据 group 内图层计算外围边框区域。 */
    private __getGroupBorderBounds(layers: ILayerProps[]) {
        const bounds = this.__getTaskBounds(layers);
        const realLayers = layers.filter((layer) => layer.layerType !== LayerType.PLACEHOLDER_LAYER);
        const borderRadiusLayers = realLayers.length ? realLayers : layers;
        const borderRadius = Math.max(
            ...borderRadiusLayers.map((layer) => layer.extraInfo?.agent?.style?.borderRadius ?? 0),
        );
        const borderGap = PLACEHOLDER_GROUP_BORDER_GAP * this.getCanvasScale();

        return {
            left: bounds.left - borderGap,
            top: bounds.top - borderGap,
            width: bounds.right - bounds.left + borderGap * 2,
            height: bounds.bottom - bounds.top + borderGap * 2,
            borderRadius: borderRadius ? borderRadius + borderGap : borderRadius,
        };
    }

    /** 整体平移 task 内所有图层，使 task 左上角对齐到目标位置。 */
    private __moveTask(task: IAgentLayoutTask, position: IPoint, arrangedExtraGroupLayerIds?: Set<string>) {
        const bounds = this.__getTaskBounds(task);
        const offsetX = position.x - bounds.left;
        const offsetY = position.y - bounds.top;

        if (!offsetX && !offsetY) return;

        task.layers.forEach((layer) => {
            this.__updateLayerPosition(
                layer,
                {
                    x: layer.left + offsetX,
                    y: layer.top + offsetY,
                },
                true,
                arrangedExtraGroupLayerIds,
            );
        });
    }

    /**
     * 将 PPT 容器排到目标位置。
     */
    private __arrangePPTContainer(options: {
        taskId?: string;
        frameIds: number[];
        containerLeft: number | null;
        containerTop: number;
    }) {
        const { edgelessPPT } = this.service;
        const padding = edgelessPPT.containerPadding;
        const { taskId, frameIds } = options;
        const containerLeft = options.containerLeft ?? 0;
        const containerTop = options.containerTop;

        if (!frameIds.length) {
            return null;
        }

        frameIds.forEach((frameId, index) => {
            const shape = this.service.frame.getShape(frameId);
            const position = edgelessPPT.getPosition(index, frameIds);

            this.__movePPTFrames([frameId], {
                x: containerLeft + padding + position.left - shape.left,
                y: containerTop + padding + position.top - shape.top,
            });
        });

        return {
            ...edgelessPPT.getContainer(frameIds),
            taskId,
            frameIds,
        };
    }

    /** 整体平移 PPT frames 与其内部图层。 */
    private __movePPTFrames(frameIds: number[], offset: IPoint) {
        frameIds.forEach((frameId) => {
            const renderIds = this.service.frame.getLayerIds(frameId);

            renderIds.forEach((layerId) => {
                const layer = this.service.flatLayer.getLayer(layerId);

                if (!layer) return;

                this.__updateLayerPosition(
                    layer,
                    {
                        x: layer.left + offset.x,
                        y: layer.top + offset.y,
                    },
                    true,
                );
            });

            this.service.frame.get(frameId)?.update();
        });
    }

    /** 通知 frame / PPT 容器 overlay 刷新，避免等待 pan 后才更新容器位置。 */
    private __notifyPPTFrameLayoutChange(hasPPTContainer: boolean) {
        if (!hasPPTContainer) return;

        const pptFrameIds = this.service.edgelessPPT.getPPTFrameIds();

        this.service.edgelessCanvas.renderAll();
        this.service.events.emit(PPT_FRAME_LAYOUT_CHANGE_EVENT, new Set(pptFrameIds));
    }

    /** 在 task 容器内按 name 分组和最大列数，逐组逐行摆放图层。 */
    private __arrangeTaskGroups(
        task: IAgentLayoutTask,
        options: Required<Pick<IAgentLayoutOptions, 'maxColumns' | 'columnGap' | 'rowGap' | 'groupGap'>> & {
            taskTop: number;
        },
        arrangedExtraGroupLayerIds?: Set<string>,
    ) {
        const { maxColumns, columnGap, rowGap, groupGap, taskTop } = options;
        let nextGroupTop: number | null = taskTop;
        let taskBottom = taskTop;

        task.groups.forEach((group) => {
            const groupTop = nextGroupTop ?? group.top;
            const rows = this.__chunkLayers(group.layers, maxColumns);
            let rowTop = groupTop;
            let groupBottom = groupTop;

            rows.forEach((row) => {
                let left = task.left;
                let rowBottom = rowTop;

                row.forEach((layer) => {
                    this.__updateLayerPosition(
                        layer,
                        {
                            x: left,
                            y: rowTop,
                        },
                        true,
                        arrangedExtraGroupLayerIds,
                    );
                    left += layer.width + columnGap;
                    rowBottom = Math.max(rowBottom, rowTop + layer.height);
                });

                groupBottom = rowBottom;
                rowTop = rowBottom + rowGap;
            });

            taskBottom = Math.max(taskBottom, groupBottom);
            nextGroupTop = groupBottom + groupGap;
        });

        return taskBottom;
    }

    /** 按最大列数将图层切分为多行。 */
    private __chunkLayers(layers: ILayerProps[], maxColumns: number) {
        const rows: ILayerProps[][] = [];

        layers.forEach((layer, index) => {
            const rowIndex = Math.floor(index / maxColumns);

            if (!rows[rowIndex]) {
                rows[rowIndex] = [];
            }

            rows[rowIndex].push(layer);
        });

        return rows;
    }

    /** 依据 task/group 最新布局结果刷新指定 frame 的 badge 缓存。 */
    private __updateBadgeMap(frameId: number, tasks: IAgentLayoutTask[]) {
        const badgeList = tasks.flatMap((task) =>
            task.groups.map((group) => {
                const groupBounds = this.__getTaskBounds(group.layers);
                const hasPlaceholderLayer = group.layers.some(
                    (layer) => layer.layerType === LayerType.PLACEHOLDER_LAYER,
                );
                const borderBounds = this.__getGroupBorderBounds(group.layers);

                return {
                    key: this.__getBadgeKey(frameId, task.taskId, group.id),
                    taskId: task.taskId,
                    groupId: group.id,
                    left: groupBounds.left,
                    top: groupBounds.top,
                    name: group.name,
                    avatar: group.layers[0]?.extraInfo?.agent?.avatar || '',
                    groupBounds: {
                        left: borderBounds.left,
                        top: borderBounds.top,
                        width: borderBounds.width,
                        height: borderBounds.height,
                        borderRadius: borderBounds.borderRadius,
                    },
                    placeholderGroupBounds: hasPlaceholderLayer
                        ? {
                              left: borderBounds.left,
                              top: borderBounds.top,
                              width: borderBounds.width,
                              height: borderBounds.height,
                              borderRadius: borderBounds.borderRadius,
                          }
                        : undefined,
                };
            }),
        );

        if (badgeList.length) {
            const previousOrderCache = this.__groupOrderCacheMap[frameId] || {};
            let maxOrder = Math.max(-1, ...Object.values(previousOrderCache));
            const nextOrderCache = {
                ...previousOrderCache,
            };

            tasks.forEach((task) => {
                task.groups.forEach((group) => {
                    const key = this.__getBadgeKey(frameId, task.taskId, group.id);

                    if (isNumber(group.layoutOrder)) {
                        nextOrderCache[key] = group.layoutOrder - 1;
                    } else if (!isNumber(nextOrderCache[key])) {
                        nextOrderCache[key] = ++maxOrder;
                    }
                });
            });

            this.__groupOrderCacheMap = {
                ...this.__groupOrderCacheMap,
                [frameId]: nextOrderCache,
            };
        }

        this.setBadgeMap({
            ...this.badgeMap,
            [frameId]: badgeList,
        });
    }
}
